import { useState, useEffect, useRef } from "react";
import ReactDOMServer from "react-dom/server";
import { isBrowser } from "^/utils/isBrowser";
import { MapMarkerIcon } from "./MapMarkerIcon";

export const createIcon = (color) => {
  if (!isBrowser) {
    return null;
  }

  // require leaflet because it is not SSR compatible and will throw an error when imported on the server
  // eslint-disable-next-line global-require
  const L = require("leaflet");

  return L.divIcon({
    html: ReactDOMServer.renderToString(<MapMarkerIcon color={color} />),
    className: "custom-marker-icon",
    iconSize: [32, 32],
    iconAnchor: [16, 32],
  });
};

export const geocodeAddress = (markerWidget) => ({
  lat: markerWidget.get("lat"),
  lon: markerWidget.get("lon"),
  address: markerWidget.get("address"),
  headline: markerWidget.get("headline") || undefined,
  color: markerWidget.get("color") || "red",
  markerWidget,
});

export const getMarkerKeys = (markers) =>
  markers
    .map(
      (m) =>
        `${m.get("address")}-${m.get("lat")}-${m.get("lon")}-${m.get(
          "color"
        )}-${m.get("link")?.url() || m.get("link")?.obj()?.id()}-${m
          .get("link")
          ?.title()}`
    )
    .join("|");

export const filterValidCoordinates = (coords) =>
  coords.filter(
    (coord) =>
      coord &&
      coord.lat !== null &&
      coord.lat !== undefined &&
      coord.lon !== null &&
      coord.lon !== undefined &&
      !Number.isNaN(coord.lat) &&
      !Number.isNaN(coord.lon)
  );

export const useOrientation = () => {
  const [isPortrait, setIsPortrait] = useState(true);
  const [resizeId, setResizeId] = useState(0);
  const resizeTimerRef = useRef();

  useEffect(() => {
    if (!isBrowser) return [false, 0];

    const updateState = () => {
      setIsPortrait(window.innerHeight >= window.innerWidth);
      setResizeId((id) => id + 1);
    };

    const debouncedUpdate = () => {
      if (resizeTimerRef.current) {
        window.clearTimeout(resizeTimerRef.current);
      }
      resizeTimerRef.current = window.setTimeout(updateState, 150);
    };

    updateState();

    window.addEventListener("resize", debouncedUpdate);
    return () => {
      window.removeEventListener("resize", debouncedUpdate);
      if (resizeTimerRef.current) {
        window.clearTimeout(resizeTimerRef.current);
      }
    };
  }, []);

  return [isPortrait, resizeId];
};

export const useMarkerCoordinates = (markers, markerKeys) => {
  const [coordinates, setCoordinates] = useState([]);

  useEffect(() => {
    const results = markers.map((markerWidget) => geocodeAddress(markerWidget));

    setCoordinates(filterValidCoordinates(results));
    // markerKeys is the stable change-detection key for markers, whose array
    // identity is never stable across renders (widget.get() returns a fresh
    // array every call), so it must be excluded here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markerKeys]);

  return coordinates;
};
